import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = [
    "profileColumn",
    "listingsColumn",
    "detailsColumn",
    "profileBar",
    "listingsBar",
    "profileCollapseButton",
    "listingsCollapseButton",
  ];

  static values = {
    storagePrefix: { type: String, default: "simulator" },
  };

  connect() {
    this.profileCollapsed = this.readState("profile");
    this.listingsCollapsed = this.readState("listings");
    this.applyState();
  }

  collapseProfile(event) {
    this.prevent(event);
    this.profileCollapsed = true;
    this.persistState("profile", true);
    this.applyState();
  }

  expandProfile(event) {
    this.prevent(event);
    this.profileCollapsed = false;
    this.persistState("profile", false);
    this.applyState();
  }

  collapseListings(event) {
    this.prevent(event);
    this.listingsCollapsed = true;
    this.persistState("listings", true);
    this.applyState();
  }

  expandListings(event) {
    this.prevent(event);
    this.listingsCollapsed = false;
    this.persistState("listings", false);
    this.applyState();
  }

  // En mode widget (#505), la colonne « Le bien idéal » est masquée : sa barre
  // repliée (`listingsBar`) n'est pas rendue. On considère alors qu'il n'existe
  // pas de colonne catalogue repliable et on garde les colonnes Profil + Détail
  // pleinement fonctionnelles (sans accéder à des cibles absentes).
  get listingsAvailable() {
    return this.hasListingsBarTarget;
  }

  applyState() {
    if (this.hasProfileColumnTarget) this.toggleColumn(this.profileColumnTarget, this.profileCollapsed);
    if (this.listingsAvailable && this.hasListingsColumnTarget) {
      this.toggleColumn(this.listingsColumnTarget, this.listingsCollapsed);
    }
    if (this.hasProfileBarTarget) this.toggleBar(this.profileBarTarget, this.profileCollapsed);
    if (this.hasListingsBarTarget) this.toggleBar(this.listingsBarTarget, this.listingsCollapsed);
    if (this.hasProfileCollapseButtonTarget) this.updateButton(this.profileCollapseButtonTarget, !this.profileCollapsed);
    if (this.listingsAvailable && this.hasListingsCollapseButtonTarget) {
      this.updateButton(this.listingsCollapseButtonTarget, !this.listingsCollapsed);
    }
    this.resizeDetailsColumn();
  }

  resizeDetailsColumn() {
    if (!this.hasDetailsColumnTarget) return;

    const profileCollapsed = this.profileCollapsed;
    // Sans colonne catalogue (mode widget), on la traite comme repliée pour que
    // la colonne Détail occupe l'espace libéré (elle ne partage la largeur
    // qu'avec la colonne Profil).
    const listingsCollapsed = this.listingsAvailable ? this.listingsCollapsed : true;

    this.detailsColumnTarget.classList.toggle("col-md-4", !profileCollapsed && !listingsCollapsed);
    this.detailsColumnTarget.classList.toggle("col-lg-6", !profileCollapsed && !listingsCollapsed);
    this.detailsColumnTarget.classList.toggle("col-md-8", profileCollapsed !== listingsCollapsed);
    this.detailsColumnTarget.classList.toggle("col-lg-9", profileCollapsed !== listingsCollapsed);
    this.detailsColumnTarget.classList.toggle("col-md-12", profileCollapsed && listingsCollapsed);
    this.detailsColumnTarget.classList.toggle("col-lg-12", profileCollapsed && listingsCollapsed);

    this.detailsColumnTarget.classList.toggle(
      "simulator-column--details-expanded",
      profileCollapsed || listingsCollapsed
    );
  }

  toggleColumn(column, collapsed) {
    column.classList.toggle("simulator-column--collapsed", collapsed);
    column.setAttribute("aria-hidden", collapsed ? "true" : "false");
    column.inert = collapsed;
  }

  toggleBar(bar, collapsed) {
    bar.hidden = !collapsed;
    bar.classList.toggle("d-none", !collapsed);
    bar.setAttribute("aria-expanded", collapsed ? "false" : "true");
  }

  updateButton(button, expanded) {
    button.setAttribute("aria-expanded", expanded ? "true" : "false");
  }

  readState(name) {
    try {
      return window.localStorage.getItem(this.storageKey(name)) === "true";
    } catch (_error) {
      return false;
    }
  }

  persistState(name, collapsed) {
    try {
      window.localStorage.setItem(this.storageKey(name), String(collapsed));
    } catch (_error) {}
  }

  storageKey(name) {
    return `${this.storagePrefixValue}.${name}Collapsed`;
  }

  prevent(event) {
    try { event?.preventDefault?.(); } catch (_error) {}
  }
}
